import {gql, useLazyQuery, useQuery} from '@apollo/client';
import {useEffect} from "react";

const GET_SEARCH_TRENDS = gql`
    fragment SearchTrendFields on SearchTrend {
        label
        sdqlCondition
        trendParameter
        isTeamTrend
        isPlayoffTrend
        hide
    }
    
    query Query(
            $sport: String, 
            $team: String, 
            $oTeam: String,
            $site: String,
            $queryString: String, 
            $line: Float, 
            $total: Float, 
            $rest: Int, 
            $oRest: Int,
            $streak: Int,
            $oStreak: Int,
            $atsStreak: Int,
            $oAtsStreak: Int,
            $ouStreak: Int,
            $oOuStreak: Int,
            $winPercentage: Float,
            $oWinPercentage: Float,
            $division: String,
            $oDivision: String,
            $conference: String,
            $oConference: String,
            $tournament: String,
            $week: Int,
            $month: Int,
            $afterAllStarBreak: Int,
            $rank: Int,
            $oRank: Int,
            $avgPace: Float,
            $avgOPace: Float, 
            $avgPoints: Float,
            $avgOPoints: Float,
            $oppAvgPoints: Float,
            $oppAvgOPoints: Float,
            $playoffs: Int,
            $round: Int,
            $seriesWins: Int,
            $seriesGame: Int,
            $noMatchupSelected: Boolean,
            $switchTeamAndOTeam: Boolean,
        ) {
      searchTrends(
        sport: $sport,
        team: $team,
        oTeam: $oTeam,
        site: $site,
        queryString: $queryString,
        line: $line,
        total: $total,
        rest: $rest,
        oRest: $oRest,
        streak: $streak,
        oStreak: $oStreak,
        atsStreak: $atsStreak,
        oAtsStreak: $oAtsStreak,
        ouStreak: $ouStreak,
        oOuStreak: $oOuStreak,
        winPercentage: $winPercentage,
        oWinPercentage: $oWinPercentage,
        division: $division,
        oDivision: $oDivision,
        conference: $conference,
        oConference: $oConference,
        tournament: $tournament,
        week: $week,
        month: $month,
        afterAllStarBreak: $afterAllStarBreak,
        rank: $rank,
        oRank: $oRank,
        avgPace: $avgPace,
        avgOPace: $avgOPace,
        avgPoints: $avgPoints,
        avgOPoints: $avgOPoints,
        oppAvgPoints: $oppAvgPoints,
        oppAvgOPoints: $oppAvgOPoints,
        playoffs: $playoffs,
        round: $round,
        seriesWins: $seriesWins,
        seriesGame: $seriesGame,
        noMatchupSelected: $noMatchupSelected,
        switchTeamAndOTeam: $switchTeamAndOTeam,
        ) {
          teamTrends {
            ...SearchTrendFields
          }
          oTeamTrends {
            ...SearchTrendFields
          }
          seasonTrends {
            ...SearchTrendFields
          }
      }
    }
`;

type GetSearchTrendsProps = {
    sport?: string;
    team?: string;
    oTeam?: string;
    site?: string;
    queryString?: string;
    line?: number;
    total?: number;
    rest?: number;
    oRest?: number;
    streak?: number;
    oStreak?: number;
    atsStreak?: number;
    oAtsStreak?: number;
    ouStreak?: number;
    oOuStreak?: number;
    winPercentage?: number;
    oWinPercentage?: number;
    division?: string;
    oDivision?: string;
    conference?: string;
    oConference?: string;
    noMatchupSelected?: boolean;
    switchTeamAndOTeam?: boolean;
    week?: number,
    month?: number,
    afterAllStarBreak?: number,
    rank?: number,
    tournament?: string,
    oRank?: number,
    avgPace?: number,
    avgOPace?: number,
    avgPoints?: number,
    avgOPoints?: number,
    oppAvgPoints?: number,
    oppAvgOPoints?: number,
    playoffs?: number,
    round?: number,
    seriesWins?: number,
    seriesGame?: number,
}

export const useGetSearchTrends = ({
        sport,
        team,
        oTeam,
        site,
        queryString,
        line,
        total,
        rest,
        oRest,
        streak,
        oStreak,
        atsStreak,
        oAtsStreak,
        ouStreak,
        oOuStreak,
        winPercentage,
        oWinPercentage,
        division,
        oDivision,
        conference,
        oConference,
        noMatchupSelected,
        week,
        month,
        afterAllStarBreak,
        rank,
        oRank,
        tournament,
        switchTeamAndOTeam,
        avgPace,
        avgOPace,
        avgPoints,
        avgOPoints,
        oppAvgPoints,
        oppAvgOPoints,
        playoffs,
        round,
        seriesWins,
        seriesGame,
}: GetSearchTrendsProps) => {


        const {called, loading, data, error, refetch} = useQuery(
            GET_SEARCH_TRENDS, {
                variables: {
                    sport,
                    line,
                    total,
                    rest,
                    site,
                    oRest,
                    streak,
                    oStreak,
                    atsStreak,
                    oAtsStreak,
                    ouStreak,
                    oOuStreak,
                    winPercentage,
                    oWinPercentage,
                    division,
                    oDivision,
                    conference,
                    oConference,
                    week,
                    month,
                    afterAllStarBreak,
                    rank,
                    oRank,
                    noMatchupSelected,
                    tournament,
                    switchTeamAndOTeam,
                    avgPace,
                    avgOPace,
                    avgPoints,
                    avgOPoints,
                    oppAvgPoints,
                    oppAvgOPoints,
                    playoffs,
                    round,
                    seriesWins,
                    seriesGame,
                },
                fetchPolicy: "no-cache"
            });

        useEffect(() => {
            if (line || line === 0) {
                refetch( {
                        sport,
                        team,
                        oTeam,
                        site,
                        queryString,
                        line,
                        total,
                        rest,
                        oRest,
                        streak,
                        oStreak,
                        atsStreak,
                        oAtsStreak,
                        ouStreak,
                        oOuStreak,
                        winPercentage,
                        oWinPercentage,
                        division,
                        oDivision,
                        conference,
                        oConference,
                        week,
                        month,
                        afterAllStarBreak,
                        rank,
                        oRank,
                        noMatchupSelected,
                        tournament,
                        switchTeamAndOTeam,
                        avgPace,
                        avgOPace,
                        avgPoints,
                        avgOPoints,
                        oppAvgPoints,
                        oppAvgOPoints,
                        playoffs,
                        round,
                        seriesWins,
                        seriesGame,
                    }
                );
            }
        }, [
            sport,
            team,
            oTeam,
            site,
            queryString,
            line,
            total,
            rest,
            oRest,
            streak,
            oStreak,
            atsStreak,
            oAtsStreak,
            ouStreak,
            oOuStreak,
            winPercentage,
            oWinPercentage,
            division,
            oDivision,
            conference,
            oConference,
            refetch,
            week,
            month,
            afterAllStarBreak,
            rank,
            oRank,
            noMatchupSelected,
            tournament,
            switchTeamAndOTeam,
            avgPace,
            avgOPace,
            avgPoints,
            avgOPoints,
            oppAvgPoints,
            oppAvgOPoints,
            playoffs,
            round,
            seriesWins,
            seriesGame,
        ]);

        return {data, loading, error};
    }
;
